import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { ArrowIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { Radio, RadioGroup } from '../../../../components/radio-group/index';
import RootLoader from '../../../../components/root-loader/index';
import { ScenarioHooks, useLocale } from '../../../../hooks/index';
import { stakeholderTypeTranslation } from '../../../../translations/stakeholder-translation.util';
import { PageRoute } from '../../../../types/page.types';
import {
  Scenario,
  ScenarioType,
  ScenarioTypeTitle,
} from '../../../../types/scenario-modeling.types';
import {
  CapTableTab,
  capTableTabTitleTitleMap,
} from '../../scenario-cap-table/fundraising/fundraising.types';
import { GeneralCapTableTab } from '../../scenario-cap-table/liquidity/liquidity.types';
import { LiquidityGraphWrapper } from '../../scenario-cap-table/liquidity/liquidity-graph/index';
import { EmptyScenarioCapTable } from '../empty-cap-table';
import { ShareClassItem } from './share-class-item';
import { StakeholderTypeItem } from './stakeholder-type-item';

export type MobileDetailsProps = {
  scenario?: Scenario;
};

export const MobileDetails: FC<MobileDetailsProps> = ({ scenario }) => {
  const [selectedDetailsTab, setDetailsTab] = useState<CapTableTab>(CapTableTab.BY_STAKEHOLDER);
  const [selectedTab, setTab] = useState<GeneralCapTableTab>(GeneralCapTableTab.CAP_TABLE);
  const { data: stakeholdersAllData, isLoading: isAllLoading } =
    ScenarioHooks.useCapTableStakeholderByAll<ScenarioType>();
  const { data: shareClassesData, isLoading: isShareClassesLoading } =
    ScenarioHooks.useCapTableShareClasses<ScenarioType>({ scenarioId: scenario?.id || '' });

  const navigate = useNavigate();
  const { messagesLocale } = useLocale();

  if (isAllLoading || isShareClassesLoading) return <RootLoader />;

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex cursor-pointer gap-2 pt-4 text-xs font-[450] text-gray-400">
        <span className="text-brand-700" onClick={() => navigate(PageRoute.SCENARIO_MODELING)}>
          <AppFormattedMessage id="MY_SCENARIOS" />
        </span>
        <span>/</span>
        <span>{scenario?.name}</span>
      </div>
      <div className="flex flex-col gap-4 border-y border-gray-100 py-3">
        <div className="flex items-center gap-2">
          <Button
            className="centered size-9 rounded-full"
            onClick={() => navigate(PageRoute.SCENARIO_MODELING)}
            style={{
              boxShadow: '0px 0px 24px 0px #00000014',
              border: '1px solid #F2F2F2',
            }}
            styleType="NONE"
          >
            <ArrowIcon />
          </Button>
          <div className="flex flex-col">
            <span className="text-base font-semibold text-gray-700">{scenario?.name}</span>
            <span className="text-xs font-[450] text-gray-400">
              <AppFormattedMessage id="DETAILS" />
            </span>
          </div>
        </div>
        {stakeholdersAllData && stakeholdersAllData?.length > 0 && (
          <div className="flex items-center gap-2">
            <div
              className={twMerge(
                'rounded-[4px] border-[1px] px-3 py-2 text-sm font-[450]',
                scenario?.type === ScenarioType.FUNDRAISING
                  ? 'border-forest-50 bg-forest-25 text-forest-600'
                  : 'border-sunbeam-50 bg-sunbeam-50 text-[#C56A08]',
              )}
            >
              <AppFormattedMessage
                id={scenario?.type ? ScenarioTypeTitle[scenario?.type] : 'FUNDRAISING_ROUND'}
              />
            </div>
            <div className="rounded-[4px] border-[1px] border-gray-50 bg-gray-50 px-3 py-2 text-sm font-[450] text-gray-700">
              {scenario?.currentValuation
                ? messagesLocale['LATEST_CAP_TABLE']
                : scenario?.valuation?.name}
            </div>
          </div>
        )}
      </div>
      {scenario?.type === ScenarioType.LIQUIDITY && (
        <div className="mb-2 flex h-[38px] w-full gap-4 border-b-[1px] border-gray-200 px-4">
          <Button
            className={twMerge(
              'flex h-full w-fit rounded-none border-b-2 border-transparent pb-4 text-sm font-[450] text-gray-400',
              selectedTab === GeneralCapTableTab.CAP_TABLE &&
                'border-brand-700 font-[550] text-brand-700',
            )}
            onClick={() => {
              setTab(GeneralCapTableTab.CAP_TABLE);
            }}
            styleType="NONE"
          >
            <AppFormattedMessage id="CAP_TABLE" />
          </Button>
          <Button
            className={twMerge(
              'flex h-full w-fit rounded-none border-b-2 border-transparent pb-4 text-sm font-[450] text-gray-400',
              selectedTab === GeneralCapTableTab.LIQUIDITY &&
                'border-brand-700 font-[550] text-brand-700',
            )}
            onClick={() => {
              setTab(GeneralCapTableTab.LIQUIDITY);
            }}
            styleType="NONE"
          >
            <AppFormattedMessage id="LIQUIDITY_DISTRIBUTION" />
          </Button>
        </div>
      )}
      {selectedTab === GeneralCapTableTab.CAP_TABLE ? (
        <>
          {stakeholdersAllData && stakeholdersAllData?.length > 0 ? (
            <>
              <div className="rounded-3xl border border-gray-200 p-1">
                <RadioGroup
                  onChange={setDetailsTab}
                  value={selectedDetailsTab}
                  wrapperClassName="w-full grow flex-1"
                >
                  {capTableTabTitleTitleMap.map(([key, title]) => (
                    <Radio
                      className="h-8 rounded-[32px] border-transparent bg-transparent !px-3 !py-2 text-xs font-[450] text-gray-700 data-[checked]:font-[550] data-[checked]:text-brand-25"
                      key={key}
                      value={title}
                    >
                      <span className="text-nowrap">
                        <AppFormattedMessage id={title} />
                      </span>
                    </Radio>
                  ))}
                </RadioGroup>
              </div>

              {selectedDetailsTab === CapTableTab.BY_STAKEHOLDER &&
                stakeholdersAllData
                  ?.filter(({ diluted }) => diluted > 0)
                  ?.map((item) => (
                    <StakeholderTypeItem
                      item={item}
                      key={item?.id}
                      name={
                        item?.type
                          ? messagesLocale[stakeholderTypeTranslation[item.type]]
                          : 'New Investors'
                      }
                    />
                  ))}

              {selectedDetailsTab === CapTableTab.BY_SHARE_CLASS &&
                shareClassesData?.map((item, i) => (
                  <ShareClassItem item={item} key={`${item?.id}_${i}`} name={item.name} />
                ))}
            </>
          ) : (
            <EmptyScenarioCapTable />
          )}
        </>
      ) : (
        <LiquidityGraphWrapper isMobile />
      )}
    </div>
  );
};
